import React, { useState, useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import { useMutation } from '@apollo/react-hooks'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  CheckoutContainer,
  ProductListContainer,
  Row,
  FlexApart,
  StoreName,
  StoreInfo,
  PromoCodeInput,
  PromoCodeBox,
  PromoDrawer,
  CampaignTerms,
} from './styled'
import { SessionContext } from '../../providers/SessionContextProvider'
import FooterButton from '../../components/FooterButton'
import {
  formatPrice, formatTotal, formatDiscount,
} from '../../utils/formatter'
import { Header as HeaderTitle, BodyCopy } from '../../components/Text'
import BasketItem from './BasketItem'
import {
  updateBasketItem, removeFromBasket, applyPromotionMutation,
} from '../../graphql/mutations'
import PoweredBy from '../../components/PoweredBy'
import IconText from '../../components/IconText'
import Color from '../../utils/color'
import Button from '../../components/Button'
import Header from '../../components/Header'
import PageLayout, { Footer } from '../PageLayout'
import Icon from '../../components/Icon'
import PickupTimer from '../../components/PickupTimer'
import { removeItemFromOptimisticBasket, updateItemInOptimisticBasket } from '../../utils/optimistic-ui'
import { handlePromoCodeError } from '../../utils/error'
import AlertModal from '../../components/Alert'

function Checkout({ history }) {
  const {
    basket, store, user, writeCache, loading,
  } = useContext(SessionContext)
  const [updateBasketItemMutation, { loading: updating }] = useMutation(updateBasketItem)
  const [removeFromBasketMutation, { loading: removing }] = useMutation(removeFromBasket)
  const [couponCode, setCouponCode] = useState('')
  const formattedCoupon = couponCode.trim().toUpperCase()
  const [applyPromoCode, { loading: applyingPromoCode }] = useMutation(applyPromotionMutation)
  const [errorMessage, setError] = useState(null)
  const [couponInputOpen, setCouponInput] = useState(false)

  const intl = useIntl()

  useEffect(() => {
    if ((basket || {}).campaign) {
      setCouponCode(basket.campaign.promotion_code)
    }
  }, [basket])

  function updateCacheBasket(newBasket) {
    if (newBasket) writeCache({ basket: newBasket })
  }

  async function removeItem(id) {
    if (basket && basket.id) {
      const { data } = await removeFromBasketMutation({
        variables: { id: basket.id, item_id: id },
        optimisticResponse: {
          removeFromBasket: removeItemFromOptimisticBasket(basket, id),
        },
        update: (cache, { data: { removeFromBasket: newBasket } }) => {
          updateCacheBasket(newBasket)
        },
      })

      const { removeFromBasket: newBasket } = data
      if (newBasket && !(newBasket.items || []).length) {
        return store ? history.push(`/store/${store.id}`) : history.goBack()
      }
    }

    return true
  }

  function updateQuantity(product, quantity) {
    if (basket && basket.id) {
      if (quantity < 1) {
        removeItem(product.id)
      } else {
        const optimisticItem = { ...product, quantity }
        updateBasketItemMutation({
          variables: {
            id: basket.id,
            item_id: product.id,
            item: { quantity, comment: product.comment },
          },
          optimisticResponse: {
            updateItem: updateItemInOptimisticBasket(basket, product, optimisticItem),
          },
          update: (cache, { data: { updateItem: newBasket } }) => {
            updateCacheBasket(newBasket)
          },
        })
      }
    }
  }

  function goToPayment() {
    history.push('/payment-select')
  }

  async function activateCoupon() {
    if (formattedCoupon) {
      try {
        await applyPromoCode({ variables: { user_id: user.id, promotion_code: formattedCoupon } })
      } catch (e) {
        const { id, vars } = handlePromoCodeError({ ...e, promotionCode: formattedCoupon, user })
        const errorMsg = intl.formatMessage({ id }, vars)
        setError(errorMsg)
      }
    }
  }

  function handleChangePromoCode(code) {
    setCouponCode(code)
  }

  function toggleCouponInput() {
    setCouponInput(prev => !prev)
  }

  const somethingInProcess = loading || updating || removing || applyingPromoCode

  return (
    <PageLayout
      header={<Header title={<FormattedMessage id="components.Header.Title.checkout" />} />}
      footer={(
        <Footer>
          <FooterButton
            disabled={somethingInProcess}
            onClick={goToPayment}
          >
            <FormattedMessage id="pages.Checkout.ToPayment" />
          </FooterButton>
        </Footer>
      )}
    >
      <CheckoutContainer>
        <FlexApart>
          <StoreInfo>
            <StoreName>{store && store.name}</StoreName>
            <IconText icon="mapPin">
              {store && ` ${store.location_name}`}
            </IconText>
            <PickupTimer />
          </StoreInfo>
          <ProductListContainer>
            {basket && (basket.items || []).map(item => (
              <BasketItem
                key={item.id}
                onChangeQuantity={updateQuantity}
                item={item}
              />
            ))}
          </ProductListContainer>

          {basket && !!basket.platform_fee && (
            <Row>
              <BodyCopy>
                <FormattedMessage id="pages.Checkout.ServiceFee" />
              </BodyCopy>
              <BodyCopy>{formatPrice(basket.platform_fee)}</BodyCopy>
            </Row>
          )}

          {basket && !!basket.campaign && (
            <Row>
              <BodyCopy color={Color.Berry}><FormattedMessage id="pages.Checkout.PromoCode" /></BodyCopy>
              <BodyCopy color={Color.Berry}>
                {`-${formatDiscount(basket.campaign)}`}
              </BodyCopy>
            </Row>
          )}

          <Row>
            <HeaderTitle>
              <FormattedMessage id="pages.Checkout.Total" />
            </HeaderTitle>
            <HeaderTitle>{formatTotal(basket)}</HeaderTitle>
          </Row>

          <PromoDrawer onClick={toggleCouponInput}>
            <HeaderTitle><FormattedMessage id="pages.PaymentSelect.PromoCode" /></HeaderTitle>
            {couponInputOpen ? <Icon name="angle_up" /> : <Icon name="angle_down" />}
          </PromoDrawer>

          {couponInputOpen && (
            <PromoCodeBox>
              <PromoCodeInput
                name="couponCode"
                value={formattedCoupon}
                onChangeText={handleChangePromoCode}
                placeholder={
                  (basket || {}).campaign && basket.campaign.terms
                    ? <FormattedMessage id="pages.PaymentSelect.CouponCodeApplied" />
                    : <FormattedMessage id="pages.PaymentSelect.CodeHere" />
                }
              />
              {(basket || {}).campaign && basket.campaign.terms && (
                <CampaignTerms>{basket.campaign.terms}</CampaignTerms>
              )}
              <Button
                loading={applyingPromoCode}
                disabled={applyingPromoCode}
                onClick={activateCoupon}
              >
                {<FormattedMessage id="pages.Checkout.Apply" />}
              </Button>
            </PromoCodeBox>
          )}
        </FlexApart>

        {errorMessage && <AlertModal message={errorMessage} onClose={() => setError(null)} />}

        <FlexApart>
          <PoweredBy />
        </FlexApart>
      </CheckoutContainer>
    </PageLayout>
  )
}

Checkout.propTypes = {
  history: PropTypes.object.isRequired,
}

export default Checkout
